import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/*
Typ: mnożenie ułamków + nawias + +/- na końcu

Postać:  A · (B ± C) ± D

- A: ułamek mieszany (1..2 + część ułamkowa) albo zwykły
- B,C,D: ułamki zwykłe właściwe i nieskracalne
- mianowniki: 2,3,4,5,6,8,10,12 (ale B,C,D właściwe)
- odpowiedź: dowolna równoważna liczbowo postać
*/

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a;
}

function reduceFrac(n, d) {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

function addFrac(A, B) {
  const D = (A.d / gcd(A.d, B.d)) * B.d;
  const n = A.n * (D / A.d) + B.n * (D / B.d);
  return reduceFrac(n, D);
}

function subFrac(A, B) {
  const D = (A.d / gcd(A.d, B.d)) * B.d;
  const n = A.n * (D / A.d) - B.n * (D / B.d);
  return reduceFrac(n, D);
}

function mulFrac(A, B) {
  return reduceFrac(A.n * B.n, A.d * B.d);
}

function fracLatex(F) {
  if (F.d === 1) return `${F.n}`;
  return `\\frac{${F.n}}{${F.d}}`;
}

const prettyFracLatex = (fraction) =>
  formatMixedFractionLatex(fraction.n, fraction.d);

function randomProperIrreducible(denPool) {
  const d = pick(denPool);
  for (let i = 0; i < 80; i++) {
    const n = 1 + Math.floor(Math.random() * (d - 1)); // 1..d-1
    if (gcd(n, d) === 1) return { n, d };
  }
  return { n: 1, d };
}

function randomMixed(wholePool, denPool) {
  const whole = pick(wholePool);
  const part = randomProperIrreducible(denPool);
  return { whole, n: part.n, d: part.d };
}

function mixedToImproper(m) {
  return reduceFrac(m.whole * m.d + m.n, m.d);
}

function mixedLatex(m) {
  return formatMixedFractionLatex(m.whole * m.d + m.n, m.d);
}

export function generateTask() {
  const denPool = [2, 3, 4, 5, 6, 8, 10, 12];

  // A: czasem mieszany, czasem zwykły (żeby było urozmaicenie)
  const AisMixed = Math.random() < 0.6; // 60% mieszany
  const Araw = AisMixed
    ? { kind: "mixed", value: randomMixed([1, 2], [2, 3, 4, 5]) } // w mieszanych trzymamy prosto: 2..5
    : { kind: "proper", value: randomProperIrreducible(denPool) };

  const Braw = { kind: "proper", value: randomProperIrreducible(denPool) };
  const Craw = { kind: "proper", value: randomProperIrreducible(denPool) };
  const Draw = { kind: "proper", value: randomProperIrreducible(denPool) };

  const opInside = pick(["+", "-"]);
  const opEnd = pick(["+", "-"]);

  const A = Araw.kind === "mixed" ? mixedToImproper(Araw.value) : reduceFrac(Araw.value.n, Araw.value.d);
  const B = reduceFrac(Braw.value.n, Braw.value.d);
  const C = reduceFrac(Craw.value.n, Craw.value.d);
  const D = reduceFrac(Draw.value.n, Draw.value.d);

  const inside = opInside === "+" ? addFrac(B, C) : subFrac(B, C);
  const product = mulFrac(A, inside);
  const result = opEnd === "+" ? addFrac(product, D) : subFrac(product, D);

  const latexA = Araw.kind === "mixed" ? mixedLatex(Araw.value) : `\\frac{${Araw.value.n}}{${Araw.value.d}}`;
  const latexB = `\\frac{${B.n}}{${B.d}}`;
  const latexC = `\\frac{${C.n}}{${C.d}}`;
  const latexD = `\\frac{${D.n}}{${D.d}}`;

  const exprLatex = `${latexA}\\cdot\\left(${latexB}${opInside}${latexC}\\right)${opEnd}${latexD}`;

  return {
    type: "FractionMultiplyBracketPlusMinusOpenTask",
    data: {
      Araw, Braw, Craw, Draw,
      A, B, C, D,
      opInside,
      opEnd,
      inside,
      product,
      result,
      exprLatex
    },
    tags: ["dzialania", "ulamki", "mnozenie_ulamkow", "kolejnosc_dzialan"]
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        Oblicz wartość:
        <span style={{ marginLeft: 10 }}>
          <InlineMath math={data.exprLatex} />
        </span>
        .
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="np. -1 3/5, -8/5, 7/12..."
      wide={true}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = userAnswer?.parsed;
  if (parsed == null || Number.isNaN(parsed)) return false;

  const expected = data.result.n / data.result.d;
  return Math.abs(parsed - expected) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={prettyFracLatex(data.result)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw, "")} />;
}

export function renderExplanation({ data }) {
  const { A, B, C, D, opInside, opEnd, inside, product, result, Araw } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      {Araw.kind === "mixed" && (
        <>
          <div><strong>Krok 0.</strong> Zamieniamy ułamek mieszany na niewłaściwy:</div>
          <BlockMath
            math={`${mixedLatex(Araw.value)}=\\frac{${A.n}}{${A.d}}`}
          />
        </>
      )}

      <div><strong>Krok 1.</strong> Liczymy nawias:</div>
      <BlockMath math={`${fracLatex(B)}${opInside}${fracLatex(C)}=${fracLatex(inside)}`} />

      <div><strong>Krok 2.</strong> Mnożymy ułamek przez wynik z nawiasu:</div>
      <BlockMath math={`${fracLatex(A)}\\cdot${fracLatex(inside)}=${fracLatex(product)}`} />

      <div><strong>Krok 3.</strong> Na końcu {opEnd === "+" ? "dodajemy" : "odejmujemy"} ostatni ułamek:</div>
      <BlockMath math={`${fracLatex(product)}${opEnd}${fracLatex(D)}=${fracLatex(result)}`} />

      <div><strong>Wynik:</strong> <InlineMath math={prettyFracLatex(result)} /></div>
    </div>
  );
}
